<template>
  <b-col cols="8" class="mx-auto">
    <!-- Multiple Machine Session Modal -->
    <b-row>
      <b-modal id="modal-confirm" hide-footer ref="confirm" title="Multiple Machine Session Found" modal-class="custom-modal">
        <b-row>
          <b-col class="p-3">
            <p class="text-sm text-slate-600 mb-4">
              <span class="font-bold text-slate-800 block text-lg mb-2">You are already signed in</span>
              We have noticed that you are currently signed in to another computer.
              For security purposes, we highly discourage having multiple sessions running simultaneously.
            </p>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="6">
            <button class="button text-white rounded-sm px-3 py-2 text-sm bg-green cursor-pointer" style="border: none;" @click="yesConfirm">
              <i class="fa fa-check-circle text-white mr-2"></i>Logout Old Session
            </button>
          </b-col>
          <b-col cols="6">
            <button class="button text-white rounded-sm px-6 py-2 text-sm bg-red cursor-pointer float-end" style="border: none;" @click="cancelConfirm">
              <i class="fa fa-times text-white mr-2"></i>Cancel
            </button>
          </b-col>
        </b-row>
      </b-modal>
    </b-row>

    <!-- Main Login Card -->
    <b-row>
      <b-col>
        <div class="login-card">
          <!-- Logo & Header -->
          <div class="flex justify-center">
            <div class="">
              <div class="text-blue text-2xl font-bold">
                URSB
              </div>
            </div>
          </div>
          
          <h2 class="text-2xl font-bold tracking-tight text-center mb-1 text-blue">eRegistry Backoffice</h2>
          
          <!-- Animated Top Divider -->
          <div class="divider-line mx-auto mb-6"></div>

          <!-- Alert message -->
          <div v-if="errorMessage != null" class="alert-custom">
            <i class="fa fa-times-circle text-lg flex-shrink-0"></i>
            <span class="text-sm font-medium">
              {{ errorMessage }}
            </span>
          </div>

          <!-- Login View -->
          <div v-if="isOnLogin">
            <p class="text-sm text-center mb-6" style="color: #64748b;">
              Please sign in to access your account
            </p>
            <form @submit.prevent="onLoginSubmit" class="space-y-4">
              <!-- Username input -->
              <div class="space-y-1.5">
                <label class="text-xs font-semibold" style="color: #64748b;">Staff Email</label>
                <div class="input-group-custom">
                  <i class="fas fa-envelope input-icon"></i>
                  <input
                      v-model="form.usernameOrEmail"
                      type="text"
                      class="input-field-custom"
                      placeholder="Enter your staff email"
                      required/>
                </div>
              </div>

              <!-- Password input -->
              <div class="space-y-1.5">
                <div class="flex justify-between items-center">
                  <label class="text-xs font-semibold" style="color: #64748b;">Password</label>
                </div>
                <div class="input-group-custom">
                  <i class="fas fa-key input-icon"></i>
                  <input
                      v-model="form.password"
                      :type="show1 ? 'text' : 'password'"
                      class="input-field-custom"
                      placeholder="Enter your password"
                      required/>
                  <i :class="show1 ? 'fas fa-eye eye-icon' : 'fas fa-eye-slash eye-icon'" @click="show1 = !show1"></i>
                </div>
              </div>

              <!-- Extra options -->
              <div class="flex items-center justify-between pt-2">
                <label class="inline-flex items-center text-sm cursor-pointer select-none">
                  <input type="checkbox" class="form-checkbox-custom"/>
                  <span class="text-xs font-medium ml-2" style="color: #64748b;">Remember me</span>
                </label>
                <div class="text-xs">
                  <router-link :to="{name:'reset-password'}" class="font-semibold text-blue-custom">
                    Forgot password?
                  </router-link>
                </div>
              </div>

              <!-- Submit button -->
              <div class="pt-2">
                <button class="btn-primary-custom" type="submit">
                  <span>Sign in</span>
                  <b-spinner v-if="showSpinner" small class="ml-2"></b-spinner>
                </button>
              </div>
            </form>
          </div>

          <!-- OTP Verification View -->
          <div v-else>
            <p class="text-sm text-center mb-6" style="color: #64748b;">
              Verify that it is you signing in. Enter the OTP code sent to your registered device.
            </p>
            <form class="space-y-6">
              <Otp ref="otp-input" :input-number="otpSize" class="mx-auto" v-on:otpData="getOtpCode"/>
              
              <div class="pt-2">
                <button
                    class="btn-primary-custom"
                    type="button"
                    @click="onOtpSubmit"
                    v-if="errorMessage == null">
                  <span>Verify Code</span>
                  <b-spinner v-if="showSpinner" small class="ml-2"></b-spinner>
                </button>

                <button
                    class="btn-secondary-custom"
                    type="button"
                    @click="onResendOtp"
                    v-else>
                  <span>Resend Code</span>
                  <b-spinner v-if="showSpinner" small class="ml-2"></b-spinner>
                </button>
              </div>
            </form>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-col>
</template>

<script>
import router from "@/router";
import {mapActions, mapGetters} from "vuex";
import jwtService from "@/app/services/jwt.service";
import {persistRefreshTokenExpiryTime} from "@/app/services/tokenRefresh";
import Otp from "@/views/auth/Login/otp/Otp";

export default {
  name: "Login",
  components: {Otp},
  data() {
    return {
      isOnLogin:true,
      show1: false,
      showSpinner:false,
      otpSize:4,
      form: {
        usernameOrEmail: null,
        password: null,
      },
      loginObject:null,
      hasError: false,
      errorMessage: null,
      otp:null,
      loading: false,
      loginTextInput: "flex-shrink flex-grow flex-auto leading-normal h-6 flex-1 self-center relative  font-roboto sm:text-sm outline-none",
    }
  },
  mounted() {
    if (sessionStorage.getItem("access_token") != null) {
      router.push("/home/dashboard")
    }
  },
  methods: {
    ...mapActions(["doLoginUser", "doOtpVerification", "doLogoutUser"]),
    onLoginSubmit() {
      this.errorMessage = null
      this.showSpinner = true
      this.doLoginUser(this.form)
    },
    onOtpSubmit() {
      this.errorMessage = null
      this.showSpinner = true
      this.doOtpVerification(this.otp)
    },
    onResendOtp() {
      //this.showSpinner = true
      this.otp = null
      this.$refs["otp-input"].flush()
      //this.doOtpVerification(this.otp)
    },
    yesConfirm(){
      this.showSpinner = true
      this.doLogoutUser(this.form)
      this.$refs.confirm.hide()
    },
    cancelConfirm(){
      this.$refs.confirm.hide()
    },
    getOtpCode(code) {
      this.showSpinner = true
      let payload = {
        token: this.getUserLoggedInState.data,
        otp: code
      }
      this.doOtpVerification(payload)
    }
  },

  computed: {
    ...mapGetters(['getUserLoggedInState','getLoginError', 'getOtpSuccess', 'getUserLogoutSuccess'])
  },
  watch: {
    getUserLoggedInState(data){
      this.successToast("Success", data.message)
      this.showSpinner = false
      this.isOnLogin = false
    },
    getOtpSuccess(loginObject){
      if(loginObject){
        jwtService.saveToken(loginObject.accessToken)
        jwtService.saveUser(loginObject.user)
        if (loginObject.refreshToken) {
          sessionStorage.setItem("tokenRefresh", loginObject.refreshToken)
        }
        persistRefreshTokenExpiryTime(
            loginObject.refreshTokenExpiryTime ||
            (loginObject.user && loginObject.user.refreshTokenExpiryTime)
        )
        this.$userProfile = loginObject.user
        this.successToast("Welcome", loginObject.user.givenName)
        let permissions = []

        loginObject.user.roles.forEach(role => {
          role.permissions.forEach(permission => {
            permissions.push({"action":permission.permission.replaceAll(" ",""),"resource":permission.group})
          })
        })
        sessionStorage.setItem('abilities',JSON.stringify(permissions))
        
        const loginChannel = new BroadcastChannel('auth-channel');
        loginChannel.postMessage({
          type: 'LOGIN',
          payload: {
            accessToken: loginObject.accessToken,
            user: loginObject.user,
            refreshToken: loginObject.refreshToken,
            refreshTokenExpiryTime: loginObject.refreshTokenExpiryTime || (loginObject.user && loginObject.user.refreshTokenExpiryTime),
            abilities: permissions
          }
        });
        loginChannel.close();

        this.showSpinner = false
        let r = this.$router.resolve({
          name: "Home",
        });
        window.location.assign(r.href);
      }
    },
    getUserLogoutSuccess(){
      this.showSpinner = true
      this.successToast("Logout", "Logged out old session")
      this.doLoginUser(this.form)
    },
    getLoginError(data){

      this.showSpinner = false
      if(data.status === 409){
        this.$refs.confirm.show();
      }else{
        //this.errorToast("Error", data.message)
        this.errorMessage = data.message
      }
    }
  }
}
</script>

<style scoped>
.login-card {
  background-color: #ffffff;
  border-radius: 1.25rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05);
  padding: 2.5rem 2.25rem;
  border: 1px solid #f1f5f9;
}

.logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background-color: rgba(0, 122, 197, 0.08);
  margin-bottom: 1.25rem;
}

.logo-circle {
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.divider-line {
  width: 3rem;
  height: 3px;
  background-color: #007ac5;
  border-radius: 9999px;
}

.alert-custom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 0.625rem;
  border: 1px solid #fee2e2;
  background-color: #fef2f2;
  color: #b91c1c;
  text-align: left;
  margin-bottom: 1.5rem;
}

.input-group-custom {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  background-color: #ffffff;
  padding: 0.625rem 0.875rem;
  transition: all 0.2s ease-in-out;
}

.input-group-custom:focus-within {
  border-color: #007ac5;
  box-shadow: 0 0 0 3px rgba(0, 122, 197, 0.12);
}

.input-icon {
  color: #94a3b8;
  margin-right: 0.75rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
}

.input-field-custom {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: transparent;
  height: 1.5rem;
}

.input-field-custom::placeholder {
  color: #94a3b8;
}

.eye-icon {
  color: #94a3b8;
  cursor: pointer;
  padding-left: 0.5rem;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.eye-icon:hover {
  color: #475569;
}

.form-checkbox-custom {
  border-radius: 0.25rem;
  border-color: #cbd5e1;
  color: #007ac5;
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.form-checkbox-custom:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #007ac5;
}

.text-blue-custom {
  color: #007ac5;
  text-decoration: none;
  transition: color 0.15s ease-in-out;
}

.text-blue-custom:hover {
  color: #005a91;
  text-decoration: underline;
}

.btn-primary-custom {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 0.625rem;
  color: #ffffff;
  background-color: #007ac5;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  outline: none;
}

.btn-primary-custom:hover {
  background-color: #0064a2;
  box-shadow: 0 4px 12px rgba(0, 122, 197, 0.15);
}

.btn-primary-custom:active {
  transform: scale(0.99);
}

.btn-secondary-custom {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 0.625rem;
  color: #dc2626;
  background-color: #ffffff;
  border: 1px solid #fca5a5;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  outline: none;
}

.btn-secondary-custom:hover {
  background-color: #fef2f2;
  border-color: #ef4444;
}

.btn-secondary-custom:active {
  transform: scale(0.99);
}
</style>